import { useCallback, useEffect, useRef, useState } from "react";

/** Versão embutida no bundle no momento do build. */
export const BUILD_VERSION: string =
  typeof __APP_BUILD_ID__ !== "undefined" ? __APP_BUILD_ID__ : "dev";

export const VERSION_CHECK_INTERVAL_MS = 5 * 60 * 1000;

/**
 * Busca a versão publicada no servidor, sem cache do navegador.
 * Retorna null quando a checagem falhar (rede instável, deploy em andamento).
 */
export async function fetchServerVersion(
  fetchImpl: typeof fetch = fetch,
): Promise<string | null> {
  try {
    const res = await fetchImpl(`/version.json?t=${Date.now()}`, {
      cache: "no-store",
    });
    if (!res.ok) return null;
    const data = await res.json();
    const version = data?.version;
    return typeof version === "string" && version.length > 0 ? version : null;
  } catch {
    return null;
  }
}

/** Só considera nova versão quando ambas são conhecidas e diferentes. */
export function isNewVersion(
  local: string | null | undefined,
  remote: string | null | undefined,
): boolean {
  if (!local || !remote) return false;
  if (local === "dev" || remote === "dev") return false;
  return local !== remote;
}

interface Options {
  intervalMs?: number;
  fetchImpl?: typeof fetch;
  localVersion?: string;
}

export function useAppVersion(options: Options = {}) {
  const {
    intervalMs = VERSION_CHECK_INTERVAL_MS,
    fetchImpl,
    localVersion = BUILD_VERSION,
  } = options;

  const [updateAvailable, setUpdateAvailable] = useState(false);
  const localRef = useRef(localVersion);
  localRef.current = localVersion;

  const check = useCallback(async () => {
    const remote = await fetchServerVersion(fetchImpl ?? fetch);
    if (isNewVersion(localRef.current, remote)) {
      setUpdateAvailable(true);
    }
  }, [fetchImpl]);

  useEffect(() => {
    let cancelled = false;
    const run = () => {
      if (!cancelled) void check();
    };

    run();
    const timer = setInterval(run, intervalMs);
    const onVisible = () => {
      if (document.visibilityState === "visible") run();
    };
    document.addEventListener("visibilitychange", onVisible);

    return () => {
      cancelled = true;
      clearInterval(timer);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [check, intervalMs]);

  return { updateAvailable, localVersion, checkNow: check };
}
